import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Alert,
  MenuItem,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined as PrizeIcon,
  ScheduleOutlined as ScheduleIcon,
  CasinoOutlined as LotteryIcon,
  SportsEsportsOutlined as MineIcon,
  AutoAwesomeOutlined as MRLIcon,
  RocketLaunchOutlined as MissileIcon,
  InfoOutlined as InfoIcon,
  MonetizationOnOutlined as CostIcon,
} from "@mui/icons-material";
import BigNumber from "bignumber.js";
import web3 from "../../../Service/web3";
import { colors, borderRadiusPx } from "../../../../theme";
import {
  CommonTextField,
  CommonDateTimePicker,
  CommonButton,
} from "../../../Components/ui";

const GAME_TYPE_CONFIG = {
  "0": {
    label: "Autobet Revolver",
    icon: <LotteryIcon sx={{ fontSize: 16, color: colors.primary.main }} />,
    desc: "Single-player: Prize rolls over automatically if nobody wins.",
  },
  "1": {
    label: "Autobet Mine",
    icon: <MineIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />,
    desc: "Mine: Results drawn automatically after all tickets sell out.",
  },
  "2": {
    label: "Autobet MRL",
    icon: <MRLIcon sx={{ fontSize: 16, color: colors.amber[600] }} />,
    desc: "Multi-round: Winners announced when ticket target met and date reached.",
  },
  "3": {
    label: "Autobet Missile",
    icon: <MissileIcon sx={{ fontSize: 16, color: colors.red[600] }} />,
    desc: "Missile: Progressive game where each rollover increases the level by one.",
  },
};

const safeFromWei = (val) => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, "ether");
  } catch (e) {
    return String(val);
  }
};

const CreateLotteryForm = ({
  lotterytype,
  setlotterytype,
  partnerlist,
  selectPartner,
  setSelectPartner,
  entryfee,
  setentryfee,
  totalPrize,
  settotalPrize,
  totalnumber,
  settotalnumber,
  capacity,
  setcapacity,
  unlockCode,
  setUnlockCode,
  startTime,
  setstartTime,
  endtime,
  setendtime,
  setdrawdate,
  Rollover,
  setRollover,
  Rolloverday,
  setRolloverdays,
  partnerPercent,
  setPartnerPercent,
  minPlayers,
  setminPlayers,
  lotteryCreateFee,
  approved,
  validated,
  loading,
  currencySymbol = "POL",
  onSubmit,
}) => {
  // Prize limit validation
  const minPrizeWei = approved?.minPrize ? Number(approved.minPrize) : 0;
  const maxPrizeWei = approved?.maxPrize ? Number(approved.maxPrize) : 0;

  const hasPrizeBounds = minPrizeWei > 0 && maxPrizeWei > 0;
  let isPrizeInBounds = true;
  if (totalPrize !== "" && hasPrizeBounds && web3?.utils?.toWei) {
    try {
      const prizeWei = Number(web3.utils.toWei(`${totalPrize}`, "ether"));
      isPrizeInBounds = prizeWei >= minPrizeWei && prizeWei <= maxPrizeWei;
    } catch {
      isPrizeInBounds = false;
    }
  }

  const creationFeeTotal = totalPrize
    ? (Number(totalPrize) * Number(lotteryCreateFee || 0)) / 100 + Number(totalPrize)
    : 0;

  const currentGame = GAME_TYPE_CONFIG[lotterytype] || GAME_TYPE_CONFIG["0"];

  return (
    <Box component="form" noValidate onSubmit={onSubmit} sx={{ width: "100%" }}>
      {/* ========================================================================= */}
      {/* Section 1: Game Type & Partner Selection */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(37, 99, 235, 0.1)",
              color: colors.primary.main,
            }}
          >
            <LotteryIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Game Type & Partner
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CommonTextField
              select
              label="Lottery Type"
              value={lotterytype}
              onChange={(event) => {
                setlotterytype(event.target.value);
                settotalnumber("1");
              }}
            >
              <MenuItem value="0">Autobet Revolver</MenuItem>
              <MenuItem value="1">Autobet Mine</MenuItem>
              <MenuItem value="2">Autobet MRL</MenuItem>
              <MenuItem value="3">Autobet Missile</MenuItem>
            </CommonTextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <CommonTextField
              select
              label="Select Partner"
              value={selectPartner}
              onChange={(e) => setSelectPartner(e.target.value)}
              disabled={partnerlist.length === 0}
              error={Boolean(validated && partnerlist.length > 0 && selectPartner === "")}
              helperText={
                validated && partnerlist.length > 0 && selectPartner === ""
                  ? "Please select a partner"
                  : partnerlist.length === 0
                  ? "No partners available"
                  : undefined
              }
            >
              {partnerlist.length === 0 ? (
                <MenuItem value="" disabled>
                  No partners available
                </MenuItem>
              ) : (
                [
                  <MenuItem key="default-empty" value="" disabled>
                    Select Partner
                  </MenuItem>,
                  ...partnerlist.map((item) => (
                    <MenuItem key={item.partnerAddress} value={item.partnerAddress}>
                      {item.name} ({item.partnerAddress.slice(0, 6)}...{item.partnerAddress.slice(-4)})
                    </MenuItem>
                  )),
                ]
              )}
            </CommonTextField>
          </Grid>
        </Grid>

        {/* Dynamic Mode Information Box */}
        <Box
          sx={{
            mt: 1.5,
            py: 1,
            px: 1.5,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.dashboard.cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {currentGame.icon}
          </Box>
          <Typography sx={{ fontSize: "12.5px", color: colors.slate[600], lineHeight: 1.4 }}>
            <strong style={{ color: colors.slate[800] }}>{currentGame.label}:</strong>{" "}
            {currentGame.desc}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

      {/* ========================================================================= */}
      {/* Section 2: Ticket & Prize Configuration */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(16, 185, 129, 0.1)",
              color: colors.emerald[600],
            }}
          >
            <PrizeIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Ticket Pricing & Prize Pool
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Ticket Fee */}
          <Grid item xs={12} sm={6}>
            <CommonTextField
              label="Ticket Fee"
              type="number"
              placeholder="0.00"
              value={entryfee}
              endAdornment={currencySymbol}
              onChange={(event) => {
                const val = event.target.value;
                setentryfee(val);
                const ep = Number(val);
                if (totalPrize !== "" && ep > 0) {
                  try {
                    const calculated = BigNumber(
                      BigNumber(totalPrize).dividedBy(ep)
                    )
                      .plus(
                        BigNumber(
                          BigNumber(
                            BigNumber(totalPrize).multipliedBy(10)
                          ).dividedBy(ep)
                        ).dividedBy(100)
                      )
                      ?.c?.[0];
                    setminPlayers(calculated ? String(calculated) : "0");
                  } catch {}
                }
              }}
              error={
                Boolean(
                  validated &&
                    (entryfee === "" ||
                      Number(entryfee) < 0 ||
                      (totalPrize !== "" && Number(entryfee) >= parseFloat(totalPrize)))
                )
              }
              helperText={
                validated && Number(entryfee) < 0
                  ? "Ticket fee cannot be negative"
                  : validated &&
                    totalPrize !== "" &&
                    Number(entryfee) >= parseFloat(totalPrize)
                  ? "Must be less than prize amount"
                  : validated && entryfee === ""
                  ? "Please enter ticket fee"
                  : undefined
              }
            />
          </Grid>

          {/* Winner Prize Amount */}
          <Grid item xs={12} sm={6}>
            <CommonTextField
              label="Winner Prize Amount"
              type="number"
              placeholder="0.00"
              value={totalPrize}
              endAdornment={currencySymbol}
              onChange={(event) => {
                const val = event.target.value;
                settotalPrize(val);
                const ep = Number(val);
                if (entryfee !== "" && Number(entryfee) > 0) {
                  try {
                    const calculated = BigNumber(
                      BigNumber(ep).dividedBy(entryfee)
                    )
                      .plus(
                        BigNumber(
                          BigNumber(
                            BigNumber(ep).multipliedBy(10)
                          ).dividedBy(entryfee)
                        ).dividedBy(100)
                      )
                      ?.c?.[0];
                    setminPlayers(calculated ? String(calculated) : "0");
                  } catch {}
                }
              }}
              error={
                Boolean(
                  validated &&
                    (totalPrize === "" ||
                      Number(totalPrize) < 0 ||
                      !isPrizeInBounds)
                )
              }
              helperText={
                validated && Number(totalPrize) < 0
                  ? "Prize amount cannot be negative"
                  : validated && totalPrize === ""
                  ? "Please enter prize amount"
                  : validated && !isPrizeInBounds && hasPrizeBounds
                  ? `Prize must be ${safeFromWei(approved?.minPrize)} - ${safeFromWei(approved?.maxPrize)} ${currencySymbol}`
                  : undefined
              }
            />
          </Grid>

          {/* Conditional: Numbers to be selected & Lottery Size OR Unlock Code Length */}
          {lotterytype !== "3" ? (
            <>
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Numbers to be selected"
                  type="number"
                  placeholder="Enter Number"
                  value={totalnumber}
                  disabled={lotterytype === "0" || lotterytype === "1"}
                  onChange={(event) => settotalnumber(event.target.value)}
                  error={
                    Boolean(
                      validated &&
                        (Number(totalnumber) < 1 ||
                          (capacity !== "" &&
                            Number(totalnumber) >= parseFloat(capacity)))
                    )
                  }
                  helperText={
                    validated && Number(totalnumber) < 1
                      ? "Must be at least 1"
                      : validated &&
                        capacity !== "" &&
                        Number(totalnumber) >= parseFloat(capacity)
                      ? "Must be less than lottery size"
                      : lotterytype === "0" || lotterytype === "1"
                      ? "Fixed to 1 for this game type"
                      : undefined
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Lottery size"
                  type="number"
                  placeholder="Enter total Numbers"
                  value={capacity}
                  onChange={(event) => setcapacity(event.target.value)}
                  error={
                    Boolean(
                      validated &&
                        (capacity === "" ||
                          ((lotterytype === "0" || lotterytype === "2") &&
                            parseInt(capacity, 10) < 5) ||
                          parseInt(capacity, 10) < 3)
                    )
                  }
                  helperText={
                    validated && capacity === ""
                      ? "Please enter lottery size"
                      : validated &&
                        (lotterytype === "0" || lotterytype === "2") &&
                        parseInt(capacity, 10) < 5
                      ? "Size must be at least 5"
                      : validated && parseInt(capacity, 10) < 3
                      ? "Size must be at least 3"
                      : undefined
                  }
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <CommonTextField
                label="Unlock Code Length"
                type="number"
                placeholder="Enter Unlock Code Length (1 - 10)"
                value={unlockCode}
                onChange={(event) => {
                  const val = event.target.value;
                  setUnlockCode(val);
                  settotalnumber(val);
                  setcapacity(val ? String(Number(val) + 1) : "");
                }}
                error={
                  Boolean(
                    validated &&
                      (unlockCode === "" ||
                        Number(unlockCode) < 1 ||
                        Number(unlockCode) > 10)
                  )
                }
                helperText={
                  validated && Number(unlockCode) < 1
                    ? "Value must be at least 1"
                    : validated && Number(unlockCode) > 10
                    ? "Value must be 10 or less"
                    : validated && unlockCode === ""
                    ? "Please enter unlock code length"
                    : "Sets dynamic code length between 1 and 10"
                }
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

      {/* ========================================================================= */}
      {/* Section 3: Schedule & Rollover Settings */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(245, 158, 11, 0.1)",
              color: colors.amber[600],
            }}
          >
            <ScheduleIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            Schedule & Rollover Settings
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Start Date */}
          <Grid item xs={12} sm={6}>
            <CommonDateTimePicker
              label="Start Date"
              value={startTime}
              onChange={(value) => setstartTime(value)}
              format="dd-MM-yy h:mma"
            />
          </Grid>

          {/* Rollover / Result Date (MRL & Missile) */}
          {(lotterytype === "2" || lotterytype === "3") && (
            <Grid item xs={12} sm={6}>
              <CommonDateTimePicker
                label="Rollover/Result Date"
                value={endtime}
                onChange={(value) => {
                  setendtime(value);
                  setdrawdate(value);
                }}
                format="dd-MM-yy h:mma"
              />
            </Grid>
          )}

          {/* Rollover in % (Revolver, MRL, Missile) */}
          {lotterytype !== "1" && (
            <Grid item xs={12} sm={6}>
              <CommonTextField
                label="Rollover in %"
                type="number"
                placeholder="0 - 50"
                value={Rollover}
                endAdornment="%"
                onChange={(event) => setRollover(event.target.value)}
                error={
                  Boolean(
                    validated &&
                      (parseFloat(Rollover) < 0 || parseFloat(Rollover) > 50)
                  )
                }
                helperText={
                  validated && parseFloat(Rollover) < 0
                    ? "Cannot be negative"
                    : validated && parseFloat(Rollover) > 50
                    ? "Cannot exceed 50%"
                    : undefined
                }
              />
            </Grid>
          )}

          {/* Rollover in Days (MRL & Missile) */}
          {(lotterytype === "2" || lotterytype === "3") && (
            <Grid item xs={12} sm={6}>
              <CommonTextField
                label="Rollover in Days"
                type="text"
                placeholder="e.g. 1"
                value={Rolloverday}
                endAdornment="Days"
                onChange={(event) => {
                  const value = event.target.value;
                  const regex = /^\d*\.?\d*$/;
                  if (regex.test(value) || value === "") {
                    setRolloverdays(value);
                  }
                }}
                error={Boolean(validated && parseFloat(Rolloverday) < 1)}
                helperText={
                  validated && parseFloat(Rolloverday) < 0
                    ? "Cannot be negative"
                    : validated && parseFloat(Rolloverday) < 1
                    ? "Must be at least 1 day"
                    : undefined
                }
              />
            </Grid>
          )}

          {/* Partner Share (if partner selected) */}
          {selectPartner !== "" && (
            <Grid item xs={12} sm={6}>
              <CommonTextField
                label="Partner's Share in %"
                type="number"
                placeholder="0 - 100"
                value={partnerPercent}
                endAdornment="%"
                onChange={(event) => setPartnerPercent(event.target.value)}
                error={
                  Boolean(
                    validated &&
                      (parseFloat(partnerPercent) < 0 ||
                        parseFloat(partnerPercent) > 100)
                  )
                }
                helperText={
                  validated && parseFloat(partnerPercent) < 0
                    ? "Cannot be negative"
                    : validated && parseFloat(partnerPercent) > 100
                    ? "Must be 0 - 100%"
                    : undefined
                }
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* ========================================================================= */}
      {/* Section 4: Cost Calculation Breakdown & Deployment Action */}
      {/* ========================================================================= */}
      <Box
        sx={{
          p: 2,
          borderRadius: borderRadiusPx.xl,
          bgcolor: colors.slate[50],
          border: `1px solid ${colors.dashboard.cardBorder}`,
          mt: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.blue[50],
                color: colors.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CostIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: colors.slate[500], textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Total Required Deposit
              </Typography>
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: colors.slate[900], lineHeight: 1.2 }}>
                {creationFeeTotal.toLocaleString(undefined, { maximumFractionDigits: 6 })} {currencySymbol}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography sx={{ fontSize: "12px", color: colors.slate[600] }}>
              Winner Prize + {lotteryCreateFee || "0"}% Protocol Creation Fee
            </Typography>
            {lotterytype === "2" && minPlayers > 0 && (
              <Typography sx={{ fontSize: "12px", color: colors.emerald[700], fontWeight: 600, mt: 0.25 }}>
                Minimum players for draw: <strong>{minPlayers}</strong>
              </Typography>
            )}
          </Box>
        </Box>

        {/* Prize bounds warning */}
        {totalPrize !== "" && hasPrizeBounds && !isPrizeInBounds && (
          <Alert
            severity="error"
            icon={<InfoIcon />}
            sx={{
              mb: 2,
              py: 0.5,
              borderRadius: borderRadiusPx.md,
              fontSize: "12.5px",
              fontWeight: 500,
            }}
          >
            Invalid winning prize amount. Allowed range:{" "}
            <strong>
              {safeFromWei(approved?.minPrize)} - {safeFromWei(approved?.maxPrize)} {currencySymbol}
            </strong>
          </Alert>
        )}

        {/* Standard CommonButton for Create Lottery */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <CommonButton
            type="submit"
            variant="contained"
            size="large"
            loading={loading}
            disabled={loading || (totalPrize !== "" && hasPrizeBounds && !isPrizeInBounds)}
            sx={{
              minWidth: { xs: "100%", sm: "220px" },
            }}
          >
            Create Lottery
          </CommonButton>
        </Box>
      </Box>
    </Box>
  );
};

CreateLotteryForm.propTypes = {
  lotterytype: PropTypes.string.isRequired,
  setlotterytype: PropTypes.func.isRequired,
  partnerlist: PropTypes.array.isRequired,
  selectPartner: PropTypes.string.isRequired,
  setSelectPartner: PropTypes.func.isRequired,
  entryfee: PropTypes.string.isRequired,
  setentryfee: PropTypes.func.isRequired,
  totalPrize: PropTypes.string.isRequired,
  settotalPrize: PropTypes.func.isRequired,
  totalnumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  settotalnumber: PropTypes.func.isRequired,
  capacity: PropTypes.string.isRequired,
  setcapacity: PropTypes.func.isRequired,
  unlockCode: PropTypes.string.isRequired,
  setUnlockCode: PropTypes.func.isRequired,
  startTime: PropTypes.instanceOf(Date).isRequired,
  setstartTime: PropTypes.func.isRequired,
  endtime: PropTypes.instanceOf(Date).isRequired,
  setendtime: PropTypes.func.isRequired,
  setdrawdate: PropTypes.func.isRequired,
  Rollover: PropTypes.string.isRequired,
  setRollover: PropTypes.func.isRequired,
  Rolloverday: PropTypes.string.isRequired,
  setRolloverdays: PropTypes.func.isRequired,
  partnerPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
  setPartnerPercent: PropTypes.func.isRequired,
  minPlayers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setminPlayers: PropTypes.func.isRequired,
  lotteryCreateFee: PropTypes.string.isRequired,
  approved: PropTypes.object,
  validated: PropTypes.bool,
  loading: PropTypes.bool,
  currencySymbol: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

export default CreateLotteryForm;
